/* eslint-disable react-refresh/only-export-components */
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { isEmpty } from 'lodash-es';
import { useParams } from 'react-router';
import { useUpdateEffect } from 'react-use';
import { RecoilRoot } from 'recoil';

import { FullStory } from '@fullstory/browser';
import * as Sentry from '@sentry/react';

import RecoilDebugger from '@/components/RecoilDebugger/RecoilDebugger';
import Loading from '@/components/utils/Loading';
import {
  AccountMemberStatus,
  CurrentClientQuery,
  CurrentSessionQuery,
  useCurrentClientQuery,
  useCurrentOrganizationUserQuery,
  useCurrentSessionQuery,
  useGetUserPermissionsForAccountQuery,
} from '@/generated';
import { RoleObject } from '@/utils/roleUtils';

import { getInvitedByName } from './getInvitedByName';
import {
  isNoAccountAccessError,
  isUserDeactivatedError,
} from './sessionErrors';

type CurrentUserAccounts = Map<
  string,
  {
    id: string;
    name: string;
    status: AccountMemberStatus;
    inviteExpiresAt?: string | null;
    invitedByName?: string;
    roles: Array<RoleObject>;
    permissions: Set<string>;
    isOwner: boolean;
    isAdmin: boolean;
  }
>;

export interface SessionContext {
  currentClient: CurrentClientQuery['currentClient'] | undefined;
  currentUser: CurrentSessionQuery['currentUser'] | undefined;
  currentUserAccounts: CurrentUserAccounts;
  clients: CurrentSessionQuery['availableClients'];
  accessType: AccessTypeEnum;
  organizationId?: string;
  accountLevelRoles?: Array<RoleObject>;
  isSuperuser: boolean;
  isOrganizationOwner: boolean;
  isOrganizationAdmin: boolean;
  isCurrentClientLoading: boolean;
  isOrgUsersLoading: boolean;
  isUserPermissionsLoading: boolean;
  hasChannelsSupport: boolean;
}

export const SessionContext = createContext<SessionContext | undefined>(
  undefined,
);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

interface SessionProviderProps {
  children: ReactNode;
}

export enum AccessTypeEnum {
  No_Account_Access,
  Access_Denied,
  Deactivated,
  Invited,
  Active,
}

const SessionProvider = ({ children }: SessionProviderProps) => {
  const [isAppReady, setIsAppReady] = useState(false);
  const { clientId: currentClientId } = useParams<{ clientId: string }>();

  const {
    data: currentSession,
    loading: isSessionLoading,
    error: sessionError,
  } = useCurrentSessionQuery();

  const { data: currentClientData, loading: isCurrentClientLoading } =
    useCurrentClientQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !currentClientId,
    });

  const currentUser = useMemo(
    () => currentSession?.currentUser,
    [currentSession?.currentUser],
  );
  const clients = useMemo(
    () => currentSession?.availableClients ?? [],
    [currentSession?.availableClients],
  );
  const currentClient = useMemo(() => {
    const client = currentClientData?.currentClient;
    if (!client) return undefined;

    return {
      ...client,
      // TODO: feature_flag_cleanup: hasToneLabelDropdown
      hasToneLabelDropdown: !client.hasSlsV2 && client.hasToneLabelDropdown,
      // TODO: feature_flag_cleanup: hasDirectCdnUpload
      hasDirectCdnUpload: client.hasDirectCdnUpload && client.hasCdnConfig,
    };
  }, [currentClientData?.currentClient]);

  const hasChannelsSupport = useMemo(() => {
    if (!currentClient) return false;
    return !!(
      currentClient.hasSMSChannel ||
      currentClient.hasMMSChannel ||
      currentClient.hasRCSChannel ||
      currentClient.hasPushChannel ||
      currentClient.hasInAppChannel
    );
  }, [currentClient]);

  const {
    data: { currentOrganizationUser: organizationUser } = {},
    loading: isOrgUsersLoading,
  } = useCurrentOrganizationUserQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !currentUser,
  });

  // Fetch account-level permissions for the current client
  const { data: userPermissionsData, loading: isUserPermissionsLoading } =
    useGetUserPermissionsForAccountQuery({
      variables: {
        clientId: currentClientId ?? '',
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !currentClientId,
    });

  const accessType = useMemo(() => {
    /**
     * These access states can surface two ways: the session query succeeds and
     * the data says so, or it fails outright with a GraphQL error naming the
     * state. Without the error checks a failed query leaves `currentUser`
     * undefined, `accessType` falls through to Active, and RouteMiddleware
     * throws ClientsNotFoundError into the app error boundary instead of
     * redirecting to the matching access-restricted screen.
     */
    if (currentUser?.isSuspended || isUserDeactivatedError(sessionError)) {
      return AccessTypeEnum.Deactivated;
    }

    if (isNoAccountAccessError(sessionError)) {
      return AccessTypeEnum.No_Account_Access;
    }

    if (!organizationUser) return AccessTypeEnum.Active;

    const hasNoAccountAccess =
      isEmpty(organizationUser.accountMembers) ||
      organizationUser.accountMembers.every(
        ({ status }) => status === 'SUSPENDED',
      );

    if (hasNoAccountAccess) {
      return AccessTypeEnum.No_Account_Access;
    }

    const hasOnlyInvitedAccounts = organizationUser.accountMembers.every(
      ({ status }) => status === 'INVITED',
    );

    if (hasOnlyInvitedAccounts) {
      return AccessTypeEnum.Invited;
    }

    return AccessTypeEnum.Active;
  }, [currentUser?.isSuspended, sessionError, organizationUser]);
  const currentUserAccounts = useMemo(() => {
    if (!organizationUser) return new Map();

    // Get permissions from the userPermissionsForAccount query for current client
    const accountPermissionsFromQuery =
      userPermissionsData?.userPermissionsForAccount?.permissions;

    return new Map(
      organizationUser.accountMembers.map(
        ({
          account,
          status,
          roles,
          isOwner,
          isAdmin,
          inviteExpiresAt,
          invitedBy,
        }) => {
          // Only store permissions for the current account (from the dedicated query)
          // Other accounts get an empty set - permissions will be fetched when switching accounts
          const permissionsSet =
            account.name === currentClient?.name && accountPermissionsFromQuery
              ? new Set(
                  accountPermissionsFromQuery.map(
                    (permission) => permission.title,
                  ),
                )
              : new Set<string>();

          return [
            account.name,
            {
              id: account.id,
              name: account.displayName,
              status,
              inviteExpiresAt,
              invitedByName: getInvitedByName(invitedBy),
              roles,
              permissions: permissionsSet,
              isOwner,
              isAdmin,
            },
          ];
        },
      ),
    );
  }, [organizationUser, currentClient, userPermissionsData]);

  const orgDetails = {
    organizationId: organizationUser?.organization?.id,
    isSuperuser: organizationUser?.isSuperuser,
    isOwner: organizationUser?.isOwner,
    isAdmin: organizationUser?.isAdmin,
  };

  // Get account-level roles by matching the current client's account name
  const accountLevelRoles = useMemo(() => {
    if (!organizationUser?.accountMembers || !currentClient?.name) {
      return undefined;
    }

    // Find the account member that matches the current client's account name
    const matchingAccountMember = organizationUser.accountMembers.find(
      (member) => member.account.name === currentClient.name,
    );

    return matchingAccountMember?.roles;
  }, [organizationUser?.accountMembers, currentClient?.name]);

  const context: SessionContext = useMemo(
    () => ({
      currentClient,
      currentUser,
      currentUserAccounts,
      clients,
      accessType,
      isCurrentClientLoading,
      isOrgUsersLoading,
      isUserPermissionsLoading,
      hasChannelsSupport,
      organizationId: orgDetails.organizationId,
      accountLevelRoles,
      isSuperuser: !!orgDetails.isSuperuser,
      isOrganizationOwner: !!orgDetails.isOwner,
      isOrganizationAdmin: !!orgDetails.isAdmin,
    }),
    [
      accessType,
      clients,
      currentClient,
      currentUser,
      currentUserAccounts,
      isCurrentClientLoading,
      isOrgUsersLoading,
      isUserPermissionsLoading,
      hasChannelsSupport,
      orgDetails.organizationId,
      orgDetails.isSuperuser,
      orgDetails.isOwner,
      orgDetails.isAdmin,
      accountLevelRoles,
    ],
  );

  useEffect(() => {
    if (!currentUser) return;
    const { fullName, email } = currentUser;
    FullStory('setIdentity', {
      uid: email,
      properties: {
        displayName: fullName,
        email: email,
      },
    });
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.id) return;
    Sentry.setUser({ id: currentUser.id });
    Sentry.setTag('clientId', currentClient?.id);
  }, [currentUser?.id, currentClient?.id]);

  const appInitLoadingStates = [
    isSessionLoading,
    isCurrentClientLoading,
    isOrgUsersLoading,
    isUserPermissionsLoading,
  ] as const;

  useUpdateEffect(() => {
    const isAppLoading = appInitLoadingStates.some((loading) => loading);

    if (isAppLoading) return;

    setIsAppReady(true);
  }, appInitLoadingStates);

  return (
    <RecoilRoot key={currentClient?.id}>
      {currentUser?.isDeveloper && <RecoilDebugger />}
      <SessionContext.Provider value={context}>
        {isAppReady ? children : <Loading height="100vh" width="100vw" />}
      </SessionContext.Provider>
    </RecoilRoot>
  );
};

export default SessionProvider;
